import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Eye, EyeOff, Calendar, Percent, Hash, TrendingUp } from 'lucide-react';
import { promoCodeService } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import Toast from '../common/Toast';

interface PromoCode {
  id: string;
  code: string;
  discountPercent: number;
  quantityIssued: number;
  usedCount: number;
  remainingCount: number;
  validFrom: string;
  validTo: string;
  isEnabled: boolean;
  totalDiscountAmount?: number;
  createdAt: string;
}

const PromoCodeManager: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState<PromoCode | null>(null);
  const [statusFilter, setStatusFilter] = useState<'active' | 'all'>('active');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    discountPercent: 10,
    quantityIssued: 100,
    validFrom: '',
    validTo: '',
  });

  useEffect(() => {
    loadPromoCodes();
  }, [statusFilter]);

  const loadPromoCodes = async () => {
    try {
      setLoading(true);
      const response = await promoCodeService.list(statusFilter);
      setPromoCodes(response.promoCodes);
    } catch (error: any) {
      showToast(error.message || 'Failed to load promo codes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromoCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await promoCodeService.create({
        code: formData.code.toUpperCase(),
        discountPercent: formData.discountPercent,
        quantityIssued: formData.quantityIssued,
        validFrom: new Date(formData.validFrom).toISOString(),
        validTo: new Date(formData.validTo).toISOString(),
      });
      
      showToast('Promo code created successfully!', 'success');
      setShowCreateModal(false);
      resetForm();
      loadPromoCodes();
    } catch (error: any) {
      showToast(error.message || 'Failed to create promo code', 'error');
    }
  };

  const handleToggleEnabled = async (code: string, currentStatus: boolean) => {
    try {
      await promoCodeService.update(code, { isEnabled: !currentStatus });
      showToast(`Promo code ${!currentStatus ? 'enabled' : 'disabled'} successfully`, 'success');
      loadPromoCodes();
    } catch (error: any) {
      showToast(error.message || 'Failed to update promo code', 'error');
    }
  };

  const handleViewDetails = async (code: string) => {
    try {
      const details = await promoCodeService.getDetails(code);
      setSelectedCode(details);
      setShowDetailsModal(true);
    } catch (error: any) {
      showToast(error.message || 'Failed to load promo code details', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discountPercent: 10,
      quantityIssued: 100,
      validFrom: '',
      validTo: '',
    });
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isExpired = (validTo: string) => {
    return new Date(validTo) < new Date();
  };

  const isActive = (validFrom: string, validTo: string, isEnabled: boolean) => {
    const now = new Date();
    return isEnabled && new Date(validFrom) <= now && new Date(validTo) >= now;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-luma-text">Promo Code Management</h1>
          <p className="text-gray-600">Create and manage discount codes for consultations</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-luma-primary text-white rounded-xl hover:bg-luma-primary-dark transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Promo Code
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setStatusFilter('active')}
          className={`px-4 py-2 font-medium transition-colors ${
            statusFilter === 'active'
              ? 'text-luma-primary border-b-2 border-luma-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Active Codes
        </button>
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 font-medium transition-colors ${
            statusFilter === 'all'
              ? 'text-luma-primary border-b-2 border-luma-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          All Codes
        </button>
      </div>

      {/* Promo Codes List */}
      <div className="grid gap-4">
        {promoCodes.length === 0 ? (
          <div className="card text-center py-12">
            <Hash className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No promo codes found</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 text-luma-primary hover:underline"
            >
              Create your first promo code
            </button>
          </div>
        ) : (
          promoCodes.map((promo) => (
            <div key={promo.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-luma-text font-mono">
                      {promo.code}
                    </h3>
                    {isActive(promo.validFrom, promo.validTo, promo.isEnabled) ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                        Active
                      </span>
                    ) : isExpired(promo.validTo) ? (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                        Expired
                      </span>
                    ) : !promo.isEnabled ? (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                        Disabled
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                        Scheduled
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 text-sm mb-1">
                        <Percent className="w-4 h-4" />
                        Discount
                      </div>
                      <p className="text-lg font-semibold text-luma-text">
                        {promo.discountPercent}%
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-1 text-gray-500 text-sm mb-1">
                        <Hash className="w-4 h-4" />
                        Usage
                      </div>
                      <p className="text-lg font-semibold text-luma-text">
                        {promo.usedCount} / {promo.quantityIssued}
                      </p>
                      <p className="text-xs text-gray-500">
                        {promo.remainingCount} remaining
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-1 text-gray-500 text-sm mb-1">
                        <Calendar className="w-4 h-4" />
                        Valid Period
                      </div>
                      <p className="text-sm text-luma-text">
                        {formatDate(promo.validFrom)}
                      </p>
                      <p className="text-sm text-gray-500">
                        to {formatDate(promo.validTo)}
                      </p>
                    </div>

                    {promo.totalDiscountAmount !== undefined && (
                      <div>
                        <div className="flex items-center gap-1 text-gray-500 text-sm mb-1">
                          <TrendingUp className="w-4 h-4" />
                          Total Savings
                        </div>
                        <p className="text-lg font-semibold text-luma-text">
                          ${promo.totalDiscountAmount.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewDetails(promo.code)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="View details"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleToggleEnabled(promo.code, promo.isEnabled)}
                    className={`p-2 rounded-lg transition-colors ${
                      promo.isEnabled
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={promo.isEnabled ? 'Disable' : 'Enable'}
                  >
                    {promo.isEnabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-luma-text mb-4">Create Promo Code</h2>
            
            <form onSubmit={handleCreatePromoCode} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Promo Code
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="input-pastel"
                  placeholder="SUMMER2024"
                  required
                  pattern="[A-Z0-9]+"
                  title="Only uppercase letters and numbers"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Percent
                </label>
                <input
                  type="number"
                  value={formData.discountPercent}
                  onChange={(e) => setFormData({ ...formData, discountPercent: parseInt(e.target.value) })}
                  className="input-pastel"
                  min="1"
                  max="100"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity Issued
                </label>
                <input
                  type="number"
                  value={formData.quantityIssued}
                  onChange={(e) => setFormData({ ...formData, quantityIssued: parseInt(e.target.value) })}
                  className="input-pastel"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valid From
                </label>
                <input
                  type="date"
                  value={formData.validFrom}
                  onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                  className="input-pastel"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valid To
                </label>
                <input
                  type="date"
                  value={formData.validTo}
                  onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
                  className="input-pastel"
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-luma-primary text-white rounded-xl hover:bg-luma-primary-dark transition-colors"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-luma-text mb-4">
              Promo Code Details: {selectedCode.code}
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Discount</p>
                  <p className="text-lg font-semibold">{selectedCode.discountPercent}%</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="text-lg font-semibold">
                    {selectedCode.isEnabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Issued</p>
                  <p className="text-lg font-semibold">{selectedCode.quantityIssued}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Used</p>
                  <p className="text-lg font-semibold">{selectedCode.usedCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Remaining</p>
                  <p className="text-lg font-semibold">{selectedCode.remainingCount}</p>
                </div>
                {selectedCode.totalDiscountAmount !== undefined && (
                  <div>
                    <p className="text-sm text-gray-500">Total Savings</p>
                    <p className="text-lg font-semibold">
                      ${selectedCode.totalDiscountAmount.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm text-gray-500">Valid Period</p>
                <p className="text-lg">
                  {formatDate(selectedCode.validFrom)} - {formatDate(selectedCode.validTo)}
                </p>
              </div>

              {(selectedCode as any).recentRedemptions && (selectedCode as any).recentRedemptions.length > 0 && (
                <div>
                  <h3 className="font-semibold text-luma-text mb-2">Recent Redemptions</h3>
                  <div className="space-y-2">
                    {(selectedCode as any).recentRedemptions.map((redemption: any) => (
                      <div key={redemption.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                        <p className="font-medium">Booking: {redemption.bookingId}</p>
                        <p className="text-gray-600">
                          Saved: ${redemption.discountAmount.toFixed(2)} 
                          ({formatDate(redemption.redeemedAt)})
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowDetailsModal(false)}
              className="mt-6 w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default PromoCodeManager;
